import type { AdminApiFunnel } from "@repo/app-types/api/funnel";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { useState, type FC } from "react";
import { Heading } from "react-aria-components";
import { useFunnelAggregatesQuery } from "../../../api/entities/funnel.js";
import { FunnelFlowChart } from "./funnelFlowChart.js";
import { FunnelStageMoments } from "./funnelStageMoments.js";

export interface FunnelReadyViewProps {
  funnel: AdminApiFunnel;
  /** The agent whose numbers are shown. */
  teammateAgentId: TeammateId;
}

export const FunnelReadyView: FC<FunnelReadyViewProps> = (props) => {
  const { funnel, teammateAgentId } = props;
  const stages = funnel.stages ?? [];
  const moments = funnel.moments ?? [];

  // Null is before the first stage.
  const [selectedStageKey, setSelectedStageKey] = useState<string | null>(
    stages[0]?.key ?? null,
  );

  const {
    data: aggregates,
    isLoading,
    isError,
  } = useFunnelAggregatesQuery(
    {
      funnelId: funnel.id,
      voiceAgentTeammateId: teammateAgentId,
    },
    // Labels land in the background; keep the numbers fresh while open.
    { pollingInterval: 60_000 },
  );

  return (
    <div className="flex flex-col gap-4">
      {isLoading ? (
        <DefaultSkeleton />
      ) : isError || !aggregates ? (
        <p className="text-sm text-red-700">
          We couldn&apos;t load this funnel&apos;s numbers. Refresh to try
          again.
        </p>
      ) : (
        <section className="space-y-6" aria-label="Interaction flow">
          <div className="space-y-1">
            <Heading
              level={2}
              className="text-base font-semibold text-foreground"
            >
              Interaction flow
            </Heading>
            <p className="text-sm text-muted-foreground">
              Follow the interactions from the last {funnel.label_window_days}{" "}
              days through each stage and see where callers leave, and with what
              outcome.
            </p>
          </div>
          <FunnelFlowChart
            stages={stages}
            stageCounts={aggregates.stages.agent}
            selectedStageKey={selectedStageKey}
            onSelectedStageKeyChange={setSelectedStageKey}
          />
          <FunnelStageMoments
            funnelId={funnel.id}
            teammateAgentId={teammateAgentId}
            stages={stages}
            moments={moments}
            aggregates={aggregates}
            stageKey={selectedStageKey}
          />
        </section>
      )}
    </div>
  );
};
